/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { allowsUser } from '../../utils/authorization';
import { createWorkPackage } from '../../actions/workPackageActions';
import { showingListWall } from '../../actions/tourActions';
import { isOwnerAuthorizedForWorkPackage } from '../../utils/workPackageHelpers';
import { acronymGenerator } from '../../utils/string';
import ItemTypes from '../../constants/itemTypes';

class AddStory extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      story: ''
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  // This component is rendered or updated every time the planwall level changes; it is also the anchor 
  // for the first step of the guided tour on each wall.  So we dispatch when it is rendered or the level changes.

  componentDidMount() {
    if (this.props.placement === 'planwall')
      this.updateListWall(this.props.level);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.placement === 'planwall' &&
      prevProps.level !== this.props.level
    )
      this.updateListWall(this.props.level);
  }

  handleChange(event) {
    this.setState({ story: event.target.value });
  }

  handleSubmit(e) {
    e.preventDefault();
    const { story } = this.state;

    if (story) {
      const { featureId, level, isNewWorkPackage, user, organizationLevel } =
        this.props;

      let { cadenceId, parentId } = this.props;

      const { visibleFieldNames, parsedFilters } =
        this.generatePlanwallFilters();

      const targetFilter =
        (visibleFieldNames.target && parsedFilters.targetFilter) || [];
      const sprintFilter =
        (visibleFieldNames.sprint && parsedFilters.sprintFilter) || [];
      const parentFilter = parsedFilters.parentFilter || [];

      const targetId =
        targetFilter.length === 1 && targetFilter[0] !== 'unassigned'
          ? targetFilter[0]
          : null;

      if (!parentId || parentId < 1)
        parentId = parentFilter.length === 1 ? parentFilter[0] : null;
      if (!cadenceId || cadenceId < 1)
        cadenceId =
          sprintFilter.length === 1 && sprintFilter[0] !== 'unassigned'
            ? sprintFilter[0]
            : null;

      const teamId = this.getTeamId(parsedFilters);
      const productId = this.getProductId(parsedFilters);

      const ownerFilter =
        (visibleFieldNames.owner && parsedFilters.ownerFilter) || [];

      let ownerId;

      if (teamId) {
        ownerId = this.authorizedOwnerId(ownerFilter, user, {
          type: 'team',
          id: teamId
        });
      } else if (!teamId && productId) {
        ownerId = this.authorizedOwnerId(ownerFilter, user, {
          type: 'product',
          id: productId
        });
      }

      const payload = {
        work_package: {
          short_name: story,
          owner_id: ownerId,
          type: ItemTypes.RESULT,
          parent_id: parentId,
          feature_id: featureId,
          product_id: productId,
          target_id: targetId,
          cadence_id: cadenceId,
          team_id: teamId,
          level
        }
      };

      if (isNewWorkPackage) {
        this.props.createTemporaryStory(payload.work_package);
      } else {
        this.props.createWorkPackage({
          data: payload,
          cadence: cadenceId,
          type: { organization_level_id: organizationLevel.id }
        });
      }
      this.setState({ story: '' });
    }
  }

  getProductId = (parsedFilters) => {
    const { selectedSpace, currentProducts, user, result, placement } =
      this.props;
    if (selectedSpace.type === 'product') {
      return selectedSpace.id;
    }
    if (selectedSpace.type === 'team') {
      const { listProductFilter } = parsedFilters;
      if (!listProductFilter) {
        if (placement.includes('result')) return result.product_id;
        return currentProducts[0]?.id || null;
      }

      if (listProductFilter[0] === 'unassigned') return null;

      if (listProductFilter.length > 1 || listProductFilter.length < 1) {
        if (
          currentProducts.some(
            (product) => product.id === user.default_product?.id
          )
        ) {
          return user.default_product.id;
        }
        return null;
      }
      if (listProductFilter.length === 1) {
        return listProductFilter[0];
      }
    }
    return null;
  };

  isDefaultTeamAuthorized = (user, currentTeams) => {
    if (
      currentTeams.some((team) => team.id === user.default_team?.id) &&
      user.default_team.permissions.includes('create_work_packages')
    ) {
      return true;
    }
    return false;
  };

  getTeamId = (parsedFilters) => {
    const { selectedSpace, user, currentTeams, result, placement } = this.props;

    // if ([1, 2].includes(level)) { <-- TODO: restore this when we support scaled teams
    if (selectedSpace.type === 'team') {
      return selectedSpace.id;
    }
    if (selectedSpace.type === 'product') {
      const { listTeamFilter } = parsedFilters;

      if (!listTeamFilter) {
        // if no team filter is selected
        if (placement.includes('result')) return result.team_id; // if component is on result modal

        if (this.isDefaultTeamAuthorized(user, currentTeams)) {
          return user.default_team.id;
        }

        return null;
      }

      if (listTeamFilter[0] === 'unassigned') return null;

      if (listTeamFilter.length > 1 || listTeamFilter.length < 1) {
        if (this.isDefaultTeamAuthorized(user, currentTeams)) {
          return user.default_team.id;
        }
        return null;
      }

      if (listTeamFilter.length === 1) return listTeamFilter[0];
    }
    // } else return null;
  };

  authorizedOwnerId = (ownerFilter, user, workPackageAuthority) => {
    const isCurrentUserAuthorized = isOwnerAuthorizedForWorkPackage(
      user.id,
      workPackageAuthority
    );
    if (
      !ownerFilter ||
      ownerFilter.length > 1 ||
      ownerFilter.length < 1 ||
      ownerFilter[0] === 'unassigned'
    ) {
      if (isCurrentUserAuthorized) return user.id;
      return null;
    }
    if (ownerFilter.length === 1) {
      if (
        isOwnerAuthorizedForWorkPackage(ownerFilter[0], workPackageAuthority)
      ) {
        return ownerFilter[0];
      }
      if (isCurrentUserAuthorized) {
        return user.id;
      }
      return null;
    }
  };

  generatePlanwallFilters = () => {
    const { planwallStates, level, placement } = this.props;

    if (placement === 'planwall') {
      const planwallState = planwallStates.find((pws) => pws.level === level);
      const currentPlanwallState = planwallState.state;

      const visibleFieldNames = currentPlanwallState.col
        ? JSON.parse(window.atob(currentPlanwallState.col))
        : {};

      const parsedFilters = currentPlanwallState.fil
        ? JSON.parse(window.atob(currentPlanwallState.fil))
        : {};
      return {
        parsedFilters,
        visibleFieldNames
      };
    }

    return {
      parsedFilters: {},
      visibleFieldNames: {}
    };
  };

  updateListWall(level) {
    this.props.showingListWall(level);
  }

  render() {
    const { story } = this.state;
    const { label, disabled, organizationLevel, cadence } = this.props;
    if (!allowsUser('create_work_packages')) return null;
    if (this.props.selectedSpace.id === 'unassigned') return null;

    let buttonText;
    if (organizationLevel && organizationLevel.name)
      buttonText = acronymGenerator(organizationLevel.name, 16);
    else buttonText = label;
    return (
      <form
        id="add-item-input"
        className={`form-inline add-story-input row ${
          cadence ? 'cadence' : ''
        }`}
        onSubmit={this.handleSubmit}
      >
        <div className="input-holder">
          <input
            value={story}
            onChange={this.handleChange}
            disabled={disabled}
          />
        </div>
        <button
          type="submit"
          className="btn btn-xs add-story pull-right"
          disabled={disabled}
        >
          <strong>ADD {buttonText}</strong>
        </button>
      </form>
    );
  }
}

AddStory.propTypes = {
  level: PropTypes.number,
  cadenceId: PropTypes.number,
  ownerId: PropTypes.number,
  parentId: PropTypes.number,
  featureId: PropTypes.number,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isNewWorkPackage: PropTypes.bool
};

AddStory.defaultProps = {
  isNewWorkPackage: false
};

const mapStateToProps = (state) => ({
  user: state.userReducer.user,
  currentUserId: state.userReducer.user.id,
  currentProducts: state.defaultOptionsReducer.currentProducts,
  currentTeams: state.defaultOptionsReducer.currentTeams,
  selectedSpace: state.workPackageReducer.selectedSpace,
  planwallStates: state.savedViewReducer.planwallStates
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ createWorkPackage, showingListWall }, dispatch);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AddStory)
);
